import { getRoomLinksWhenPlatformIsEKA } from "./../../apis/tryst/tele-be-v2/useCreateRoomLinksForAID";
import {
  APPOINTMENT_CHANNEL,
  APPOINTMENT_STATUS,
  MODES_OF_APT,
  SupportedTeleProviders,
  TYPES_OF_APT,
} from "@elixir/types";
import { logAndGetErrorResponse } from "../error";
import { ControllerReturnType } from "../types";
import {
  cancelAppointmentReminders,
  createAppointment,
  getIdb,
  getMe,
  sendAppointmentCommunication,
  updateAppointment,
} from "../../utils/bridges/methods";
import generateAppointmentId from "./generate-appointment-id";
import dayjs from "dayjs";
import { convertDateToFirestoreTimestamp } from "../../firestore/utils";
import { MESSAGE_TYPE } from "../../utils/bridges/events/types";
import {
  trackAppointmentActivity,
  AptActivityType,
} from "./appointment-activity";
import { FirebaseAppointment } from "../../firestore/types";
import getB from "../../utils/bridges/EkaBridge";

const urlFormatter = (baseUrl: string, QPkey: string, QPvalue: string) => {
  try {
    const cleaned = new URL(baseUrl);
    cleaned.searchParams.set(QPkey, QPvalue);
    return cleaned.toString();
  } catch (e) {
    return baseUrl;
  }
};

export default async function rescheduleAppointment({
  aid,
  startTime,
  endTime,
  updatedDoctorOid,
  channel,
  clinicId,
  mode,
}: RescheduleSlotParams): Promise<ControllerReturnType<FirebaseAppointment>> {
  try {
    const appointment = await getIdb().appointments.get({ aid });
    if (!appointment) {
      throw new Error("Could not find the appointment to reschedule");
    }

    const { payment_id, payment_status, doctor_oid } = appointment;
    const myProfile = getMe();

    const isDoctorOfApt = doctor_oid === myProfile.user.oid;

    const updateAppointmentRequest = updateAppointment({
      aid,
      data: {
        status: isDoctorOfApt
          ? APPOINTMENT_STATUS.DOCTOR_RESCHEDULED
          : APPOINTMENT_STATUS.STAFF_RESCHEDULED,
        payment_status: null,
        payment_id: null,
      },
    });

    cancelAppointmentReminders({ aid });
    trackAppointmentActivity({
      aid,
      activity: AptActivityType.VISIT_RESCHEDULED,
    });

    const rescheduleAppointmentId = generateAppointmentId();

    const date = convertDateToFirestoreTimestamp(
      dayjs(startTime).startOf("day").toDate()
    );
    let finalVcMeta;
    let finalMode = mode || appointment.mode;
    console.log(finalMode, "finalMode");

    if (finalMode === "tele") {
      const shouldCreate =
        !appointment.vc_meta || appointment.vc_meta?.platform === "eka";
      if (shouldCreate) {
        const data = await getRoomLinksWhenPlatformIsEKA(
          rescheduleAppointmentId
        );
        const [patientData] =
          (await getB().getPatientsByIds([appointment.patient_oid])) || [];
        console.log(patientData, "patientData from bridge");

        const docProfile = myProfile.doctors.find(
          (item) => item.oid === (updatedDoctorOid || appointment.doctor_oid)
        );

        if (data?.guest_url) {
          finalVcMeta = {
            platform: SupportedTeleProviders.eka,
            meet_link: urlFormatter(
              data.guest_url,
              "name",
              patientData.personal.name.f
            ),
            host_link: urlFormatter(
              data.host_url,
              "name",
              docProfile?.name as string
            ),
          };
        }
      } else {
        finalVcMeta = appointment.vc_meta;
      }
    }
    console.log(finalVcMeta, "finalVcMeta before writing");
    const rescheduledAptRequest = createAppointment({
      aid: rescheduleAppointmentId,
      data: {
        aid: rescheduleAppointmentId,
        doctor_oid: updatedDoctorOid || appointment.doctor_oid,
        clinic_id: clinicId || appointment.clinic_id,
        archive: false,
        bid: appointment.bid,
        comm_meta: [],
        date,
        full_date: convertDateToFirestoreTimestamp(new Date(startTime)),
        start_time: convertDateToFirestoreTimestamp(new Date(startTime)),
        end_time: convertDateToFirestoreTimestamp(new Date(endTime)),
        visit_type: TYPES_OF_APT.REGULAR,
        expires_at: convertDateToFirestoreTimestamp(
          dayjs(startTime).add(1, "year").toDate()
        ),
        patient_oid: appointment.patient_oid,
        patient_uuid: appointment.patient_uuid,
        receipts: [],
        status: APPOINTMENT_STATUS.BOOKED,
        apt_fields: [],
        assessments: {},
        channel: appointment.channel || channel,
        last_visit_time: epochTimeToTimestamp(appointment.last_visit_time),
        visit_count: appointment.visit_count,
        mode: finalMode,
        payment_id,
        payment_status,
        vc_meta: finalVcMeta,
        p_aid: aid,
        client_payment_id: appointment.client_payment_id,
        client_pt_id: appointment.client_pt_id,
        client_visit_id: appointment.client_visit_id,
        meta_data: appointment.meta_data,
      },
    });

    const [rescheduledAppointment] = await Promise.all([
      rescheduledAptRequest,
      updateAppointmentRequest,
    ]);

    //TODO - Add activity update

    cancelAppointmentReminders({ aid });
    trackAppointmentActivity({
      aid,
      activity: AptActivityType.VISIT_RESCHEDULED,
    });
    sendAppointmentCommunication({
      aid: rescheduleAppointmentId,
      data: { msg_type: MESSAGE_TYPE.RESCHEDULE_APPOINTMENT },
    });
    sendAppointmentCommunication({
      aid: rescheduleAppointmentId,
      data: { msg_type: MESSAGE_TYPE.SCHEDULE_APT_REMIDNER },
    });
    trackAppointmentActivity({
      aid: rescheduleAppointmentId,
      activity: AptActivityType.RESCHEDULE_CREATED,
    });
    return { success: true, data: rescheduledAppointment };
  } catch (e) {
    return logAndGetErrorResponse(e);
  }
}

function epochTimeToTimestamp(epoch?: number | null) {
  if (!epoch) {
    return;
  }
  return convertDateToFirestoreTimestamp(new Date(epoch));
}

export type RescheduleSlotParams = {
  aid: string;
  startTime: string;
  endTime: string;
  updatedDoctorOid?: string;
  channel: APPOINTMENT_CHANNEL;
  clinicId: string;
  mode: MODES_OF_APT;
};
